import { useState } from 'react'

import { PlanConsistencyStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import type { PlanHandlingMemo } from '@ebalance/proto/bffuser/v1/submission_management_model_pb'
import { useTranslation } from 'react-i18next'

import {
  AccordionRoot,
  Box,
  type BoxProps,
  AccordionItem as EcAccordionItem,
  AccordionItemContent as EcAccordionItemContent,
  AccordionItemTrigger as EcAccordionItemTrigger,
  HStack,
  Input,
  LightModeSemanticColors,
  SecondaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AccordionDisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import { FieldErrorText, FieldRoot } from '~/components/common/Field'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { PlanConsistencyStatusBadge } from '~/components/common/PlanConsistencyStatusBadge'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { ResolveCounterpartyMismatch } from '~/pages/ProcurementPlanDetail/components/ResolveCounterpartyMismatch'
import { useGetPlanCounterpartyMismatch } from '~/pages/ProcurementPlanDetail/hooks/useGetPlanCounterpartyMismatch'
import { useListPlanHandlingMemos } from '~/pages/ProcurementPlanDetail/hooks/useListPlanHandlingMemos'
import { useRecordPlanHandlingMemo } from '~/pages/ProcurementPlanDetail/hooks/useRecordPlanHandlingMemo'
import { formatTimestamp } from '~/pages/ProcurementPlanDetail/models/formatTimestamp'
import {
  HANDLING_MEMO_MAX_LENGTH,
  type HandlingMemoValidationError,
  countHandlingMemoLength,
  normalizeHandlingMemoBody,
  validateHandlingMemo,
} from '~/pages/ProcurementPlanDetail/models/handlingMemo'
import { hasUnresolvedMismatch } from '~/pages/ProcurementPlanDetail/models/hasUnresolvedMismatch'
import type { PlanTarget } from '~/pages/ProcurementPlanDetail/models/planTarget'

// chakra v3.29 + React 18 types 既知不具合ワークアラウンド
// (SpotPrice/index.tsx・ColumnGuideTable.tsx 同形)。
const AccordionItem = EcAccordionItem as React.FC<BoxProps & { value: string }>
const AccordionItemTrigger = EcAccordionItemTrigger as React.FC<BoxProps>
const AccordionItemContent = EcAccordionItemContent as React.FC<BoxProps>

const ITEM_VALUE = 'handlingMemo'

type InputError = HandlingMemoValidationError | 'saveFailed'

const INPUT_ERROR_KEY: Record<InputError, string> = {
  empty: 'page:procurementPlanDetail.handlingMemo.error.empty',
  tooLong: 'page:procurementPlanDetail.handlingMemo.error.tooLong',
  saveFailed: 'page:procurementPlanDetail.handlingMemo.error.saveFailed',
}

/** 記録のたびに送るメモの id。backend はこの id で冪等に記録し、手動の再試行 (AC-PSM-059) で消せないメモ (AC-PSM-061) が重複しないようにする。 */
const newMemoId = (): string => crypto.randomUUID()

type Props = {
  /**
   * 対象の計画。版の詳細は計画提出 ID (= route param `:id`)、eBalance に提出記録の
   * 無い計画の詳細 (AC-PPD-028) は業務キー。
   */
  target: PlanTarget
}

/**
 * 対応メモパネル (spec submission-management.md §7、AC-PSM-057 / 057a / 058 / 059 /
 * 061 / 068 / 069。design: prototype `js/plan-mismatch-memo.js`、Des #9552)。
 *
 * DetailHeader 直下の DS accordion。見出し行に整合ステータスのバッジ (AC-PPD-023)・「対応メモ」・件数・解消ボタン
 * (AC-PSM-063 の `ResolveCounterpartyMismatch`。AC-PSM-068「入力欄は解消ボタンと
 * 同じ場所」) を並べ、本文に履歴と入力欄を置く。
 *
 * - 表示条件: Get (解消 read) と List (メモ read) のどちらかが `featureEnabled` の時。
 *   解消ボタンは Get の結果だけで出し分ける (List の loading / 失敗に左右されない)
 * - 初期の開閉: Get が確定した時点で未解消の取引相手間ミスマッチがあれば開く。以後は
 *   利用者の開閉に従う (解消後に勝手に畳まない)
 * - 編集・削除の UI は持たない (AC-PSM-061)
 */
export const HandlingMemoPanel = ({
  target,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const mismatch = useGetPlanCounterpartyMismatch(target)
  const memos = useListPlanHandlingMemos(target)
  const resolveMemberName = useMemberDisplayName()
  const { record, isRecording } = useRecordPlanHandlingMemo(target)

  const [draft, setDraft] = useState('')
  const [memoId, setMemoId] = useState(newMemoId)
  const [inputError, setInputError] = useState<InputError | undefined>(
    undefined,
  )
  const [open, setOpen] = useState<boolean | undefined>(undefined)

  const hasUnresolved = hasUnresolvedMismatch(mismatch.data)
  // AC-PPD-023: 整合ステータスのバッジ。取得前・取得失敗・機能 OFF・UNSPECIFIED (判定材料が
  // 無い) では出さない (判定材料が無い時に「整合」と言わない)。
  const consistencyStatus =
    mismatch.data?.featureEnabled === true &&
    mismatch.data.consistencyStatus !== PlanConsistencyStatus.UNSPECIFIED
      ? mismatch.data.consistencyStatus
      : undefined
  // Get が確定 (成功 / 失敗) した最初の描画で初期の開閉を決める。
  if (open === undefined && (mismatch.isSuccess || mismatch.isError)) {
    setOpen(hasUnresolved)
  }

  const isVisible =
    mismatch.data?.featureEnabled === true ||
    memos.data?.featureEnabled === true
  if (!isVisible) {
    return null
  }

  const rows = memos.data?.memos ?? []
  const draftLength = countHandlingMemoLength(normalizeHandlingMemoBody(draft))

  const columns: Array<MasterDataTableColumn<PlanHandlingMemo>> = [
    {
      key: 'postedAt',
      header: t('page:procurementPlanDetail.handlingMemo.postedAt'),
      nowrap: true,
      cell: (m) => formatTimestamp(m.postedAt),
    },
    {
      key: 'postedBy',
      header: t('page:procurementPlanDetail.handlingMemo.postedBy'),
      nowrap: true,
      // 解決できない投稿者は「—」。member id (auth0 subject) は出さない
      cell: (m) => resolveMemberName(m.postedBy) ?? '—',
    },
    {
      key: 'body',
      header: t('page:procurementPlanDetail.handlingMemo.label'),
      cell: (m) => m.body,
    },
  ]

  const handleRecord = (): void => {
    if (isRecording) return
    const validation = validateHandlingMemo(draft)
    if (validation !== undefined) {
      setInputError(validation)
      return
    }
    setInputError(undefined)
    const body = normalizeHandlingMemoBody(draft)
    record(
      { memoId, body },
      {
        onSuccess: () => {
          // 通信中に打ち足した未送信の入力は消さない (AC-PSM-057: 入力中の内容は未保存として扱う)
          setDraft((current) =>
            normalizeHandlingMemoBody(current) === body ? '' : current,
          )
          setMemoId(newMemoId())
        },
        // AC-PSM-059: 入力を残し、同じ本文の再試行では同じ memoId を送る
        onError: () => {
          setInputError('saveFailed')
        },
      },
    )
  }

  return (
    <AccordionRoot
      collapsible
      // chakra v3.29 + React 18 types の既知不具合で `AccordionRoot` の props が
      // `any` に解決されるため、引数の形をここで明示する (SpotPrice/index.tsx 同形)。
      onValueChange={(details: { value: string[] }) => {
        setOpen(details.value.includes(ITEM_VALUE))
      }}
      value={open === true ? [ITEM_VALUE] : []}
    >
      {/* 器の枠・角丸は design `.ecl-accordion` 準拠 (SpotPrice/index.tsx の同コメント参照) */}
      <AccordionItem
        overflow="hidden"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="lg"
        backgroundColor={LightModeSemanticColors.Surface.primary.value}
        value={ITEM_VALUE}
      >
        <Box position="relative">
          <AccordionItemTrigger paddingX={4} paddingY={3}>
            <AccordionDisclosureIcon />
            {/* 見出しの 1 行に 整合ステータス・対応メモ・解消ボタンを並べる (design
                `js/plan-mismatch-memo.js` の summary 行、Des #9552) */}
            {consistencyStatus !== undefined && (
              <HStack as="span" gap={2} marginRight={2}>
                <Text
                  as="span"
                  textStyle="accordionMeta"
                  color="Text.midEmphasis"
                  whiteSpace="nowrap"
                >
                  {t(
                    'page:procurementPlanDetail.handlingMemo.consistencyStatus',
                  )}
                </Text>
                <PlanConsistencyStatusBadge status={consistencyStatus} />
              </HStack>
            )}
            <Text textStyle="sectionTitle">
              {t('page:procurementPlanDetail.handlingMemo.label')}
            </Text>
            {/* 件数。spec 付録に「N件」の文言が無く共通の件数キーも無いため数字だけを出す
                (付録への追加は issue #9798 で PdM に依頼済み)。 */}
            {memos.data?.featureEnabled === true && (
              <Text
                as="span"
                textStyle="accordionMeta"
                color="Text.midEmphasis"
                whiteSpace="nowrap"
              >
                {rows.length}
              </Text>
            )}
          </AccordionItemTrigger>
          {/* 解消ボタンは trigger (= <button>) の外に absolute で重ねる。中に入れると
              button-in-button になり、click が accordion の開閉にも伝わる
              (SpotPrice/index.tsx の overlay と同じ扱い)。 */}
          <Box
            position="absolute"
            zIndex={1}
            top={0}
            right={4}
            bottom={0}
            alignItems="center"
            display="flex"
          >
            <ResolveCounterpartyMismatch target={target} />
          </Box>
        </Box>
        <AccordionItemContent
          padding={4}
          borderTopWidth="1px"
          borderTopColor="Border.lowEmphasis"
        >
          <VStack align="stretch" gap={3}>
            <MasterDataTable
              columns={columns}
              errorLabel={
                memos.isError
                  ? t('page:procurementPlanDetail.error.loadFailed')
                  : undefined
              }
              getRowKey={(m) => m.id}
              isLoading={memos.isPending && memos.fetchStatus !== 'idle'}
              rows={rows}
            />
            <FieldRoot invalid={inputError !== undefined}>
              <HStack align="center" gap={2} width="100%">
                {/* DS Input は flex item として伸びないので、幅を持つ Box で包む (design: 入力欄は行幅いっぱい) */}
                <Box flex="1 1 0" minWidth={0}>
                  <Input
                    width="100%"
                    aria-label={t(
                      'page:procurementPlanDetail.handlingMemo.label',
                    )}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setDraft(e.target.value)
                      // 保存される本文 (前後の空白を除いたもの) が変わったら別のメモとして
                      // 新しい id を使う。空白だけの変更は同じメモの再試行 (AC-PSM-059)
                      if (
                        normalizeHandlingMemoBody(e.target.value) !==
                        normalizeHandlingMemoBody(draft)
                      ) {
                        setMemoId(newMemoId())
                      }
                      setInputError(undefined)
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (
                        e.key !== 'Enter' ||
                        e.nativeEvent.isComposing ||
                        e.keyCode === 229
                      ) {
                        return
                      }
                      e.preventDefault()
                      handleRecord()
                    }}
                    size="sm"
                    value={draft}
                  />
                </Box>
                <SecondaryButton
                  disabled={isRecording}
                  loading={isRecording}
                  onClick={handleRecord}
                  size="sm"
                >
                  {t('page:procurementPlanDetail.handlingMemo.record')}
                </SecondaryButton>
              </HStack>
              <HStack align="flex-start" gap={3} width="100%">
                <Box flex="1 1 auto">
                  {inputError !== undefined && (
                    <FieldErrorText>
                      {t(INPUT_ERROR_KEY[inputError])}
                    </FieldErrorText>
                  )}
                </Box>
                <Text
                  textStyle="captionNormal"
                  color={
                    draftLength > HANDLING_MEMO_MAX_LENGTH
                      ? 'Text.negativeMid'
                      : 'Text.midEmphasis'
                  }
                  whiteSpace="nowrap"
                  aria-hidden
                  fontVariantNumeric="tabular-nums"
                >
                  {t('page:procurementPlanDetail.handlingMemo.counter', {
                    count: draftLength,
                    max: HANDLING_MEMO_MAX_LENGTH,
                  })}
                </Text>
              </HStack>
            </FieldRoot>
          </VStack>
        </AccordionItemContent>
      </AccordionItem>
    </AccordionRoot>
  )
}
